import { createSelector, createReducer } from '@reduxjs/toolkit';
import {
  selectAllFromApiState,
  getIncludedData,
  getRequestData,
} from '../helpers/selectorHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  INVITATIONGROUP_TYPE,
  INVITATIONKEY_INCLUDE,
} from '../../constants/api';
import {
  addInvitationKeysToStore,
  getAllInvitationGroupsSuccess,
  removeInvitationKeysFromStore,
  updateInvitationKeysInitialize,
} from '../actions/api';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { all, call, fork, takeEvery } from 'redux-saga/effects';
import { generateCreateApiCall } from '../helpers/sagaHelpers';
import { InvitationKeyConnector } from '../connectors/invitationKeyConnector';
import { API_REDUCER } from '../../constants/store';

// -------- Overview --------

// The following endpoints exist
//  - PATCH /{invitationGroupId}/InvitationKeys

// InvitationKey is included in the following endpoints
//  - GET /InvitationGroup

// -------- Reducer --------
const invitationKeyReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(getAllInvitationGroupsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationKeys = data.filter(
          (include) => include.type === INVITATIONKEY_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationKeys);
      })
      .addCase(addInvitationKeysToStore, (state, action) => {
        const invitationKeys = getRequestData(action);
        return updateDtoList(state, 'all', invitationKeys);
      })
      .addCase(removeInvitationKeysFromStore, (state, action) => {
        const idsToRemove = action.payload;
        return removeItemFromList(state, 'all', idsToRemove);
      });
  },
);

// -------- Sagas ---------
export function* invitationKeySaga() {
  yield all([fork(updateInvitationKeySaga)]);
}

function* updateInvitationKeySaga() {
  yield takeEvery(
    updateInvitationKeysInitialize.type,
    generateCreateApiCall(InvitationKeyConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.updateInvitationKeys],
        action.payload.invitationGroupId,
        action.payload.dtos,
      );
    }),
  );
}

// -------- Selectors --------
const selectGetAllInvitationKeys = (state) =>
  selectAllFromApiState(state, API_REDUCER.INVITATION_KEY);

const allInvitationKeysSelector = createSelector(
  selectGetAllInvitationKeys,
  (invitationKeys) => invitationKeys,
);

const invitationKeyIdsByInvitationGroupId = (invitationGroupId) =>
  createSelector(selectGetAllInvitationKeys, (invitationKeys) => {
    const invitationKeysByInvitationGroupId = invitationKeys.filter(
      (invitationKey) =>
        getRelId(invitationKey, INVITATIONGROUP_TYPE) === invitationGroupId,
    );
    return invitationKeysByInvitationGroupId.map((invitationGroupMapping) =>
      getEntityId(invitationGroupMapping),
    );
  });

// -------- Exporting --------
export { allInvitationKeysSelector, invitationKeyIdsByInvitationGroupId };
export default invitationKeyReducer;
